/**
 * Havenwear product.js - product page components.
 * Depends on theme.js (loaded first with defer).
 */
(() => {
  'use strict';

  const theme = window.theme;
  const { $, $$, define } = theme.utils;
  const money = (cents) => theme.formatMoneyShort(cents);

  /* ---------------------------------------------------------------------
     Media gallery: thumbnails, swipe track, dots, hover zoom, lightbox
     --------------------------------------------------------------------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      if (!this.track) return;
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('[data-thumb-index]', this);
      this.dots = $$('[data-dot-index]', this);
      this.index = 0;

      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumbIndex))));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.dotIndex))));
      this.track.addEventListener('scroll', theme.utils.debounce(() => this.syncFromScroll(), 60), { passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        }
      });

      if (this.dataset.zoom === 'true') this.initZoom();
      if ($('.gallery__slide--model', this) && window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0' }, { name: 'shopify-xr', version: '1.0' }]);
      }
    }

    goTo(index, smooth = true) {
      if (!this.slides.length) return;
      const target = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.scrollTo({
        left: target * this.track.clientWidth,
        behavior: smooth && !theme.utils.reducedMotion() ? 'smooth' : 'auto',
      });
      this.setActive(target);
    }

    goToMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1) this.goTo(index);
    }

    syncFromScroll() {
      const index = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
      if (index !== this.index) this.setActive(index);
    }

    setActive(index) {
      this.index = index;
      this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
      this.slides.forEach((slide, i) => {
        if (i === index) return;
        $$('video', slide).forEach((video) => video.pause());
      });
    }

    initZoom() {
      const info = this.closest('product-info');
      const modal = info && document.getElementById(`Zoom-${info.dataset.section}`);
      const canHover = window.matchMedia('(hover: hover) and (min-width: 1024px)');
      $$('[data-zoom-open]', this).forEach((button) => {
        button.addEventListener('click', () => {
          if (!modal) return;
          modal.open(button);
          const item = modal.querySelector(`[data-zoom-index="${button.dataset.zoomOpen}"]`);
          if (item) window.setTimeout(() => item.scrollIntoView({ block: 'start' }), 60);
        });
        button.addEventListener('mousemove', (event) => {
          if (!canHover.matches) return;
          const rect = button.getBoundingClientRect();
          button.style.setProperty('--zoom-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
          button.style.setProperty('--zoom-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
          button.classList.add('is-zooming');
        });
        button.addEventListener('mouseleave', () => button.classList.remove('is-zooming'));
      });
    }
  }

  define('media-gallery', MediaGallery);

  /* ---------------------------------------------------------------------
     Sticky add to cart (also toggles to "Notify me" when sold out)
     --------------------------------------------------------------------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.info = this.closest('product-info');
      this.button = $('[data-sticky-button]', this);
      this.label = $('[data-sticky-label]', this);
      this.desktop = window.matchMedia('(min-width: 1024px)');
      this.anchor = this.form ? $('[data-add-button]', this.form) : null;
      this.anchorVisible = true;
      this.anchorAbove = false;

      this.sheet = $('[data-sticky-sheet]', this);
      this.sheetToggle = $('[data-sticky-variant-toggle]', this);

      if (this.button) this.button.addEventListener('click', () => this.onClick());

      if (this.sheet && this.sheetToggle) {
        this.sheetToggle.addEventListener('click', () => this.toggleSheet());
        this.sheet.addEventListener('click', (event) => {
          const option = event.target.closest('[data-sticky-option]');
          if (option) this.chooseOption(option);
        });
        this.onDocumentClick = (event) => {
          if (this.sheetOpen && !this.contains(event.target)) this.closeSheet();
        };
        this.onKeydown = (event) => {
          if (event.key === 'Escape' && this.sheetOpen) {
            this.closeSheet();
            this.sheetToggle.focus({ preventScroll: true });
          }
        };
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        this.syncSheet();
      }

      if (this.anchor && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.anchorVisible = entry.isIntersecting;
          this.anchorAbove = entry.boundingClientRect.top < 0;
          this.sync();
        });
        this.observer.observe(this.anchor);
      } else {
        this.onScroll = theme.utils.onFrame(() => this.sync());
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
      if (this.desktop.addEventListener) this.desktop.addEventListener('change', () => this.sync());
      this.sync();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
    }

    /* ---- Size sheet ----
       The sheet does not own any variant logic. It ticks the matching radio in the main
       variant picker and lets product-info drive price, availability and media as usual. */
    get sheetOpen() {
      return !!this.sheet && !this.sheet.hidden;
    }

    get picker() {
      return this.info ? $('[data-variant-picker]', this.info) : null;
    }

    toggleSheet() {
      if (this.sheetOpen) this.closeSheet();
      else this.openSheet();
    }

    openSheet() {
      if (!this.sheet) return;
      this.sheet.hidden = false;
      this.classList.add('is-sheet-open');
      this.sheetToggle.setAttribute('aria-expanded', 'true');
      const current = $('[data-sticky-option][aria-pressed="true"]', this.sheet);
      if (current) current.focus({ preventScroll: true });
    }

    closeSheet() {
      if (!this.sheet) return;
      this.sheet.hidden = true;
      this.classList.remove('is-sheet-open');
      this.sheetToggle.setAttribute('aria-expanded', 'false');
    }

    chooseOption(button) {
      const picker = this.picker;
      const position = button.dataset.stickyOption;
      const value = button.dataset.stickyValue;
      if (picker) {
        const input = $$('.option-input', picker).find(
          (el) => el.value === value && el.name.endsWith('-option-' + position)
        );
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.syncSheet();
      this.closeSheet();
      this.sheetToggle.focus({ preventScroll: true });
    }

    syncSheet() {
      const picker = this.picker;
      if (!this.sheet || !picker) return;
      const chosen = {};
      $$('.option-input:checked', picker).forEach((input) => {
        chosen[input.name.split('-option-').pop()] = input.value;
      });
      $$('[data-sticky-option]', this.sheet).forEach((button) => {
        const selected = chosen[button.dataset.stickyOption] === button.dataset.stickyValue;
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    get mode() {
      return this.desktop.matches ? this.dataset.desktop : this.dataset.mobile;
    }

    sync() {
      let visible = false;
      if (this.mode === 'always') {
        visible = true;
      } else if (this.mode === 'scroll') {
        visible = this.observer ? !this.anchorVisible && this.anchorAbove : window.scrollY > 640;
      }
      this.classList.toggle('is-visible', visible);
      if (visible) this.removeAttribute('inert');
      else this.setAttribute('inert', '');
    }

    update(variant, quantity = 1) {
      $$('[data-sticky-price]', this).forEach((el) => {
        el.textContent = variant ? money(variant.price) : '';
      });
      const variantLabel = $('[data-sticky-variant]', this);
      if (variantLabel && variant) variantLabel.textContent = variant.title;
      this.syncSheet();
      if (!this.label) return;
      if (!variant) {
        this.label.textContent = theme.strings.unavailable;
      } else if (!variant.available) {
        this.label.textContent = this.button.dataset.notifyLabel;
      } else {
        const base = (this.anchor && this.anchor.dataset.label) || theme.strings.addToCart;
        this.label.textContent = `${base} — ${money(variant.price * quantity)}`;
      }
    }

    onClick() {
      const addButton = this.anchor;
      if (!addButton || !this.form) {
        window.location.href = theme.routes.cart;
        return;
      }
      if (addButton.disabled) {
        const notify = this.info && $('[data-notify]', this.info);
        const target = notify && !notify.hidden ? notify : this.info && $('[data-variant-picker]', this.info);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const email = notify && !notify.hidden ? $('[data-notify-email]', notify) : null;
        if (email) window.setTimeout(() => email.focus({ preventScroll: true }), 400);
        return;
      }
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(addButton);
      else addButton.click();
    }
  }

  define('sticky-atc', StickyAtc);

  /* ---------------------------------------------------------------------
     Complete the look / frequently bought together
     --------------------------------------------------------------------- */
  class FbtBundle extends HTMLElement {
    connectedCallback() {
      this.total = $('[data-fbt-total]', this);
      this.addButton = $('[data-fbt-add]', this);
      this.addEventListener('change', () => this.updateTotal());
      if (this.addButton) this.addButton.addEventListener('click', () => this.add());
      this.updateTotal();
    }

    setCurrentPrice(price) {
      const current = $('[data-fbt-current]', this);
      const label = $('[data-fbt-current-price]', this);
      if (current) current.dataset.price = price;
      if (label) label.textContent = money(price);
      this.updateTotal();
    }

    items() {
      const items = [];
      if (this.dataset.includeCurrent === 'true') {
        const form = document.getElementById(this.dataset.formId);
        const variantInput = form && $('[data-variant-input]', form);
        const addButton = form && $('[data-add-button]', form);
        const current = $('[data-fbt-current]', this);
        if (variantInput && current && addButton && !addButton.disabled) {
          items.push({ id: Number(variantInput.value), quantity: 1, price: Number(current.dataset.price) });
        }
      }
      $$('.fbt__item:not(.fbt__item--current)', this).forEach((row) => {
        const check = $('[data-fbt-check]', row);
        const variant = $('[data-fbt-variant]', row);
        if (!variant) return;
        const option = variant.tagName === 'SELECT' ? variant.selectedOptions[0] : variant;
        const price = Number(option && option.dataset.price) || 0;
        const priceLabel = $('[data-fbt-price]', row);
        if (priceLabel) priceLabel.textContent = money(price);
        if (check && check.checked && option && !option.disabled) {
          items.push({ id: Number(variant.value), quantity: 1, price });
        }
      });
      return items;
    }

    updateTotal() {
      const items = this.items();
      if (this.total) this.total.textContent = money(items.reduce((sum, item) => sum + item.price, 0));
      if (this.addButton) this.addButton.disabled = items.length === 0;
    }

    async add() {
      const items = this.items().map(({ id, quantity }) => ({ id, quantity }));
      if (!items.length) return;
      this.addButton.classList.add('is-loading');
      try {
        await theme.cartApi.add({ items });
        const title = $('.pdp__title');
        theme.cartApi.afterAdd(title ? title.textContent.trim() : '', this.addButton);
      } catch (error) {
        theme.toast(error.message);
      } finally {
        this.addButton.classList.remove('is-loading');
      }
    }
  }

  define('fbt-bundle', FbtBundle);

  /* ---------------------------------------------------------------------
     Tabs / accordion (tabs on tablet+ when style is "tabs")
     --------------------------------------------------------------------- */
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this.toggles = $$('.tabs__toggle', this);
      this.tabs = $$('[role="tab"]', this);
      this.toggles.forEach((toggle) => {
        toggle.addEventListener('click', () => this.setPanel(toggle, toggle.getAttribute('aria-expanded') !== 'true'));
      });
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => {
          const last = this.tabs.length - 1;
          let next = null;
          if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
          if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = last;
          if (next === null) return;
          event.preventDefault();
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }

    setPanel(toggle, open) {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;
      toggle.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      const item = toggle.closest('.tabs__item');
      if (item) item.classList.toggle('is-open', open);
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
        if (this.toggles[i]) this.toggles[i].setAttribute('aria-expanded', String(selected));
      });
    }
  }

  define('product-tabs', ProductTabs);

  /* ---------------------------------------------------------------------
     Recommendations (section + in-buy-box complementary list)
     --------------------------------------------------------------------- */
  const whenVisible = (element, callback) => {
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        callback();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    observer.observe(element);
  };

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.loaded) return;
      whenVisible(this, () => this.load());
    }

    async load() {
      this.loaded = true;
      try {
        const response = await fetch(this.dataset.url);
        const doc = theme.utils.parseHTML(await response.text());
        const fresh = $('product-recommendations', doc);
        if (fresh && $('.product-grid', fresh)) {
          this.innerHTML = fresh.innerHTML;
          this.classList.add('is-loaded');
        }
      } catch (error) {
        /* Recommendations are progressive enhancement. */
      }
    }
  }

  define('product-recommendations', ProductRecommendations);

  class ComplementaryProducts extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.loaded) return;
      this.hidden = true;
      whenVisible(this, () => this.load());
    }

    async load() {
      this.loaded = true;
      try {
        const response = await fetch(this.dataset.url);
        const doc = theme.utils.parseHTML(await response.text());
        const template = $('template[data-compact]', doc);
        const list = $('[data-complementary-list]', this);
        if (!template || !list || !template.content.children.length) return;
        list.appendChild(document.importNode(template.content, true));
        this.hidden = false;
      } catch (error) {
        /* Leave hidden. */
      }
    }
  }

  define('complementary-products', ComplementaryProducts);

  /* ---------------------------------------------------------------------
     Product info: variant selection, price, stock, media, URL
     --------------------------------------------------------------------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = $('[data-product-json]', this);
      if (!json) return;
      try {
        this.product = JSON.parse(json.textContent);
      } catch (error) {
        return;
      }
      this.form = document.getElementById(this.dataset.formId);
      this.variantInput = this.form ? $('[data-variant-input]', this.form) : null;
      this.quantityInput = this.form ? $('input[name="quantity"]', this.form) : null;
      this.picker = $('[data-variant-picker]', this);
      const initialId = this.variantInput ? Number(this.variantInput.value) : null;
      this.variant = this.product.variants.find((variant) => variant.id === initialId) || this.product.variants[0];

      if (this.picker) {
        this.picker.addEventListener('change', (event) => {
          if (event.target.matches('.option-input')) this.onOptionChange();
        });
      }
      if (this.quantityInput) this.quantityInput.addEventListener('change', () => this.updateAddLabel());

      const buyNow = $('[data-buy-now]', this);
      if (buyNow) buyNow.addEventListener('click', () => this.buyNow());

      this.onTrigger = (event) => {
        const trigger = event.target.closest('[data-trigger-atc]');
        if (trigger) this.triggerAdd();
      };
      document.addEventListener('click', this.onTrigger);

      this.markAvailability();
      this.updateAddLabel();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTrigger);
    }

    selectedOptions() {
      return $$('fieldset[data-option-position]', this.picker).map((fieldset) => {
        const checked = $('.option-input:checked', fieldset);
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const options = this.selectedOptions();
      $$('fieldset[data-option-position]', this.picker).forEach((fieldset, index) => {
        const label = $('[data-option-selected]', fieldset);
        if (label) label.textContent = options[index] || '';
      });
      this.variant = this.product.variants.find((variant) => variant.options.every((value, index) => value === options[index])) || null;
      this.markAvailability();
      this.render();
    }

    markAvailability() {
      if (!this.picker) return;
      const options = this.selectedOptions();
      $$('fieldset[data-option-position]', this.picker).forEach((fieldset, index) => {
        $$('.option-input', fieldset).forEach((input) => {
          const available = this.product.variants.some(
            (variant) =>
              variant.available &&
              variant.options[index] === input.value &&
              options.slice(0, index).every((value, i) => variant.options[i] === value)
          );
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !available);
        });
      });
    }

    render() {
      const variant = this.variant;
      const addButton = $('[data-add-button]', this);
      const buyNow = $('[data-buy-now]', this);
      const notify = $('[data-notify]', this);

      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (buyNow) buyNow.disabled = true;
        const label = $('[data-add-label]', this);
        if (label) label.textContent = theme.strings.unavailable;
        this.updateSticky(null);
        return;
      }

      if (this.variantInput) this.variantInput.value = variant.id;
      if (!theme.designMode) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      this.renderPrice(variant);
      this.renderBadge(variant);

      if (addButton) addButton.disabled = !variant.available;
      if (buyNow) buyNow.disabled = !variant.available;
      if (notify) {
        notify.hidden = variant.available;
        const notifyVariant = $('[data-notify-variant]', notify);
        if (notifyVariant) notifyVariant.value = variant.title;
      }

      this.renderStock(variant);

      if (variant.featured_media_id) {
        const gallery = $('media-gallery', this);
        if (gallery && typeof gallery.goToMedia === 'function') gallery.goToMedia(variant.featured_media_id);
      }

      const bundle = $('fbt-bundle', this);
      if (bundle && typeof bundle.setCurrentPrice === 'function') bundle.setCurrentPrice(variant.price);

      this.updateAddLabel();
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    renderPrice(variant) {
      const container = $('[data-product-price]', this);
      if (!container) return;
      const root = $('[data-price]', container);
      const current = $('[data-price-current]', container);
      const compare = $('[data-price-compare]', container);
      const saving = $('[data-price-saving]', container);
      const saleLabel = $('[data-price-sale-label]', container);
      const onSale = variant.compare_at_price > variant.price;
      if (root) {
        root.classList.toggle('price--on-sale', onSale);
        root.classList.toggle('price--sold-out', !variant.available);
      }
      if (current) current.textContent = money(variant.price);
      if (compare) {
        compare.hidden = !onSale;
        compare.textContent = onSale ? money(variant.compare_at_price) : '';
      }
      if (saleLabel) saleLabel.hidden = !onSale;
      if (saving) {
        saving.hidden = !onSale;
        if (onSale) {
          const percent = Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
          saving.textContent = (saving.dataset.template || '').replace('[percent]', percent);
        }
      }
    }

    renderBadge(variant) {
      const badge = $('[data-media-badge]', this);
      if (!badge || badge.dataset.badgeStyle === 'custom') return;
      const onSale = variant.compare_at_price > variant.price;
      badge.hidden = !onSale;
      if (!onSale) return;
      if (badge.dataset.badgeStyle === 'percent') {
        const percent = Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
        badge.textContent = (badge.dataset.percentTemplate || '').replace('[percent]', percent);
      } else {
        badge.textContent = (badge.dataset.badgeTemplate || '').replace('[amount]', theme.formatMoneyShort(variant.compare_at_price - variant.price));
      }
    }

    renderStock(variant) {
      const stock = $('[data-low-stock]', this);
      if (!stock) return;
      const threshold = Number(this.dataset.lowStockThreshold) || 0;
      const show = this.dataset.lowStockEnabled === 'true' && variant.inventory !== null && variant.inventory > 0 && variant.inventory <= threshold;
      stock.hidden = !show;
      const text = $('[data-low-stock-text]', stock);
      if (show && text) text.textContent = (stock.dataset.template || '').replace('[count]', variant.inventory);
    }

    updateAddLabel() {
      const addButton = $('[data-add-button]', this);
      const label = $('[data-add-label]', this);
      const quantity = Math.max(1, parseInt(this.quantityInput ? this.quantityInput.value : 1, 10) || 1);
      if (addButton && label && this.variant) {
        if (!this.variant.available) {
          label.textContent = theme.strings.soldOut;
        } else if (addButton.dataset.showPrice === 'true') {
          label.textContent = addButton.dataset.label;
          const priceSpan = document.createElement('span');
          priceSpan.className = 'btn__price';
          priceSpan.textContent = ` — ${money(this.variant.price * quantity)}`;
          label.appendChild(priceSpan);
        } else {
          label.textContent = addButton.dataset.label;
        }
      }
      this.updateSticky(this.variant, quantity);
    }

    updateSticky(variant, quantity = 1) {
      const sticky = $('sticky-atc', this);
      if (sticky && typeof sticky.update === 'function') sticky.update(variant, quantity);
    }

    buyNow() {
      const wrapper = this.form && this.form.closest('product-form');
      const addButton = $('[data-add-button]', this);
      if (!wrapper || !addButton || addButton.disabled) return;
      wrapper.buyNow = true;
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(addButton);
      else addButton.click();
    }

    triggerAdd() {
      const addButton = $('[data-add-button]', this);
      if (!addButton || !this.form) return;
      if (addButton.disabled) {
        const target = $('[data-notify]:not([hidden])', this) || this.picker || this.form;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(addButton);
      else addButton.click();
    }
  }

  define('product-info', ProductInfo);
})();
